/**
 * Utilidad para que los paneles laterales de Univer (atajos de teclado,
 * encabezado y pie, comentarios, funciones, validación, formato
 * condicional, etc.) sean cerrables de forma fiable.
 *
 * Estructura real del sidebar en Univer 0.4.2:
 *   .univer-sidebar.univer-sidebar-open
 *     └─ .univer-sidebar-container
 *           ├─ .univer-sidebar-header
 *           │     ├─ title
 *           │     └─ .univer-sidebar-header-close   ← close nativo
 *           ├─ .univer-sidebar-body
 *           └─ .univer-sidebar-footer
 *
 * Problemas observados:
 *   1. El `.univer-sidebar-header-close` existe en el DOM, pero no todos
 *      los paneles (p.ej. Encabezado y Pie) le conectan un callback: al
 *      clicarlo no pasa nada.
 *   2. Los toolbar-btn no exponen ningún estado "activo": no podemos
 *      localizar a posteriori el botón que abrió el panel, hay que
 *      recordarlo en el momento del click.
 *   3. Ni click-fuera ni ESC cierran el panel por defecto.
 *
 * Estrategia (v4):
 *   a. Capture-phase mousedown: memorizamos el último toolbar-btn clicado
 *      con timestamp.
 *   b. MutationObserver (sin filtro — las mutaciones relevantes son raras)
 *      detecta cuando un `.univer-sidebar` entra en estado `-open`.
 *      Al detectar apertura fijamos `currentOpener = last toolbar-btn si
 *      fue hace <600ms`.
 *   c. Hijackamos `.univer-sidebar-header-close` nativo: su click llama a
 *      nuestro `tryClose()` (que usa múltiples estrategias en cascada).
 *   d. `tryClose()`:
 *        1º clic simulado en el close nativo (si todavía no lo hemos
 *           marcado como "ya probado, no funciona").
 *        2º click en el `currentOpener` memorizado (toggle-close).
 *        3º selectores activos (por si Univer añade estado en el futuro).
 *        4º Hide CSS como último recurso.
 *   e. Click fuera del sidebar + ESC disparan `tryClose()`.
 *
 * Uso:
 *   const cleanup = wireUniverPanelClose(mountEl);
 *   // luego beforeDestroy:
 *   cleanup();
 */

const OPENER_WINDOW_MS = 800;
const HIJACK_MARK = '__ibbHijacked';

// Estado módulo-scoped
let lastToolbarBtn = null;
let lastToolbarAt = 0;
let currentOpener = null;
let sidebarIsOpen = false;

// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

function findToolbarBtnInPath(path) {
  for (const node of path) {
    if (!node || !node.classList) continue;
    if (
      node.classList.contains('univer-toolbar-btn') ||
      node.classList.contains('univer-toolbar-item-select')
    ) {
      return node;
    }
  }
  return null;
}

function isInPathClass(path, classNames) {
  for (const node of path) {
    if (!node || !node.classList) continue;
    for (const cn of classNames) {
      if (node.classList.contains(cn)) return true;
    }
  }
  return false;
}

function getOpenSidebar() {
  return document.querySelector('.univer-sidebar.univer-sidebar-open');
}

function getNativeCloseBtn(sidebar) {
  return sidebar ? sidebar.querySelector('.univer-sidebar-header-close') : null;
}

// -------------------------------------------------------------------------
// Close strategies
// -------------------------------------------------------------------------

function tryClose() {
  const sidebar = getOpenSidebar();
  if (!sidebar) return;

  const stillOpen = () => !!getOpenSidebar();

  // 1) Click en el close nativo (que Univer cablea al SidebarService.close).
  //    Casi todos los paneles lo tienen y funciona bien. Los raros que no
  //    (algunos en Sheets/Docs con onClose sin callback) pasan al paso 2.
  const nativeClose = getNativeCloseBtn(sidebar);
  if (nativeClose && !sidebar.dataset.ibbNativeUseless) {
    try {
      sidebar.dataset.ibbClosing = '1';
      nativeClose.click();
      delete sidebar.dataset.ibbClosing;
      if (!stillOpen()) return;
      sidebar.dataset.ibbNativeUseless = '1';
    } catch (_) {
      delete sidebar.dataset.ibbClosing;
    }
  }

  // 2) Re-click al toolbar-btn que abri\u00f3 el panel (toggle-close).
  //    S\u00f3lo si sabemos con seguridad que fue un toolbar-BUTTON (no un
  //    dropdown-select: esos vuelven a abrir el men\u00fa y dejan el panel
  //    activo).
  if (currentOpener && currentOpener.isConnected &&
      currentOpener.classList.contains('univer-toolbar-btn')) {
    try {
      currentOpener.click();
      if (!stillOpen()) return;
    } catch (_) { /* noop */ }
  }

  // 3) Fallback CSS. Liberamos al usuario del bloqueo visual.
  //    IMPORTANTE: no hacemos click "a ciegas" en botones activos del
  //    toolbar — v2 de esta utilidad hac\u00eda eso y acab\u00f3 apagando
  //    cosas como el toggle de cuadr\u00edcula (que siempre est\u00e1 en
  //    estado active).
  sidebar.classList.remove('univer-sidebar-open');
  sidebar.classList.add('ibb-sidebar-forced-close');
  sidebarIsOpen = false;
  currentOpener = null;
}

// -------------------------------------------------------------------------
// Hijack del close nativo
// -------------------------------------------------------------------------

function hijackNativeClose(sidebar) {
  const closeBtn = getNativeCloseBtn(sidebar);
  if (!closeBtn || closeBtn[HIJACK_MARK]) return;
  closeBtn[HIJACK_MARK] = true;
  // Visibilidad: hacemos el botón más grande y obvio.
  closeBtn.classList.add('ibb-sidebar-header-close');
  closeBtn.setAttribute('title', 'Cerrar');
  closeBtn.setAttribute('aria-label', 'Cerrar');

  // Interceptamos en captura para adelantarnos al listener nativo.
  closeBtn.addEventListener('click', (evt) => {
    // Si nosotros mismos estamos disparando el close nativo desde
    // tryClose(), dejamos pasar sin recursión.
    const sb = evt.currentTarget.closest('.univer-sidebar');
    if (sb && sb.dataset.ibbClosing === '1') return;
    evt.preventDefault();
    evt.stopPropagation();
    tryClose();
  }, true);

  closeBtn.addEventListener('mousedown', (evt) => {
    // Evitar que nuestro handler global "click fuera" interprete este
    // click (aunque como está dentro del sidebar ya se ignora, esto es
    // defensa en profundidad).
    evt.stopPropagation();
  }, true);
}

// -------------------------------------------------------------------------
// Listeners globales
// -------------------------------------------------------------------------

function onGlobalMouseDown(evt) {
  const path = evt.composedPath ? evt.composedPath() : [];

  const btn = findToolbarBtnInPath(path);
  if (btn) {
    lastToolbarBtn = btn;
    lastToolbarAt = Date.now();
  }

  if (!sidebarIsOpen) return;

  // Click dentro del sidebar → no cerrar.
  if (isInPathClass(path, ['univer-sidebar'])) return;

  // Click en toolbar → que lo gestione Univer (toggle o cambio de panel).
  if (isInPathClass(path, ['univer-toolbar', 'univer-toolbar-btn', 'univer-toolbar-item-select'])) {
    return;
  }

  // Click fuera → cerrar.
  tryClose();
}

function onGlobalKeyDown(evt) {
  if (evt.key === 'Escape' && sidebarIsOpen) {
    evt.preventDefault();
    tryClose();
  }
}

// -------------------------------------------------------------------------
// Handler del MutationObserver
// -------------------------------------------------------------------------

function handleSidebarMutation() {
  // Univer puede re-abrir un sidebar que previamente escondimos con el
  // fallback CSS (`ibb-sidebar-forced-close`). En cuanto vemos que Univer
  // le a\u00f1ade `univer-sidebar-open`, limpiamos nuestra clase for\u00e1nea
  // para que vuelva a ser visible. Tambi\u00e9n limpiamos `ibbNativeUseless`
  // porque con el nuevo render el close nativo puede estar conectado a otro
  // callback.
  const allSidebars = document.querySelectorAll('.univer-sidebar');
  allSidebars.forEach(s => {
    if (s.classList.contains('univer-sidebar-open') &&
        s.classList.contains('ibb-sidebar-forced-close')) {
      s.classList.remove('ibb-sidebar-forced-close');
    }
    if (s.classList.contains('univer-sidebar-open') && s.dataset.ibbNativeUseless) {
      delete s.dataset.ibbNativeUseless;
    }
  });

  const sidebar = getOpenSidebar();
  const nowOpen = !!sidebar;

  if (nowOpen) {
    hijackNativeClose(sidebar);
  }

  if (nowOpen && !sidebarIsOpen) {
    sidebarIsOpen = true;
    if (lastToolbarBtn && lastToolbarBtn.isConnected &&
        Date.now() - lastToolbarAt < OPENER_WINDOW_MS) {
      currentOpener = lastToolbarBtn;
    } else {
      currentOpener = null;
    }
  } else if (nowOpen && sidebarIsOpen) {
    // Cambio de panel mientras hay uno abierto → re-correlacionar.
    if (lastToolbarBtn && lastToolbarBtn.isConnected &&
        Date.now() - lastToolbarAt < OPENER_WINDOW_MS &&
        lastToolbarBtn !== currentOpener) {
      currentOpener = lastToolbarBtn;
    }
  } else if (!nowOpen && sidebarIsOpen) {
    sidebarIsOpen = false;
    currentOpener = null;
  }
}

// -------------------------------------------------------------------------
// API pública
// -------------------------------------------------------------------------

export function wireUniverPanelClose(mountEl) {
  if (!mountEl) return () => {};

  // Reset para SPA (puede montarse/desmontarse varias veces)
  lastToolbarBtn = null;
  lastToolbarAt = 0;
  currentOpener = null;
  sidebarIsOpen = !!getOpenSidebar();

  const observer = new MutationObserver(() => handleSidebarMutation());
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  // Estado inicial
  const existing = getOpenSidebar();
  if (existing) hijackNativeClose(existing);

  document.addEventListener('mousedown', onGlobalMouseDown, true);
  document.addEventListener('keydown', onGlobalKeyDown, true);

  return function cleanup() {
    try { observer.disconnect(); } catch (_) { /* noop */ }
    document.removeEventListener('mousedown', onGlobalMouseDown, true);
    document.removeEventListener('keydown', onGlobalKeyDown, true);
    lastToolbarBtn = null;
    lastToolbarAt = 0;
    currentOpener = null;
    sidebarIsOpen = false;
  };
}
